import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  X,
  Package as PackageIcon,
  HelpCircle,
  Building2,
  Code2,
  Sprout,
  Crown,
  TrendingUp,
  Rocket,
  Puzzle,
  ShoppingCart,
  Warehouse,
  Wallet,
  Landmark,
  FileText,
  Link2,
  Tag,
  Smartphone,
  ShoppingBag,
  Sliders,
  Eye,
  Headset,
  ExternalLink,
  Info,
  type LucideIcon,
} from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import { formatMoney } from "@finora/config";
import { ApiError } from "@finora/api-client";
import { api } from "../../lib/api";
import { KasaLpfrCard } from "./KasaLpfrCard";

type SalesDocumentDto = Awaited<ReturnType<typeof api.changePackage>>;

const STATUS_LABELS: Record<string, string> = {
  TRIAL: "Probni period",
  ACTIVE: "Aktivan",
  PAST_DUE: "Kasni sa plaćanjem",
  CANCELLED: "Otkazan",
  EXPIRED: "Istekao",
  SUSPENDED: "Suspendovan",
};

const SUPPORT_MAILTO = "mailto:podrska@hafiskal.rs";

/**
 * Redizajn "Moj paket" stranice (22.09.2026, po dogovoru i priloženom
 * mockup-u; DORAĐENO istog dana posle povratne informacije - vidi
 * razgovor) - dizajn (kartice paketa, grid dodatnih modula sa prekidačima,
 * "Vaš izbor" sažetak) je preuzet iz mockup slike; PODACI su i dalje
 * realni - dolaze iz api.getMyPackage() (`current`/`packages`/
 * `addonModules`/`activeModules`, vidi subscription.service.ts).
 *
 * ISPRAVKE posle prve verzije (22.09.2026, isti dan):
 * 1) "Izaberi/Promeni paket" i prekidač modula VIŠE NE kreiraju ponudu
 *    odmah - samo STAGE-uju izbor u "Vaš izbor" panel (stagedPackageId /
 *    stagedAddonKeys). Ponuda(e) se kreiraju TEK na "Sačuvaj promene".
 *    Razlog: korisnik treba da vidi/uporedi dostupne module PRE nego što
 *    ga sistem odvede na naplatu.
 * 2) `current` (Subscription) je NULL za firme koje još nemaju Subscription
 *    red (stara/legacy firma - vidi EntitlementService, "postojeće firme
 *    NEMAJU Subscription"). Ranije je to izgledalo kao da stranica "ne zna"
 *    koji je paket firme. Sada se u svakom slučaju prikazuje
 *    `activeModules` (puna lista trenutno aktivnih modula, iz ISTOG izvora
 *    koji stvarno kontroliše pristup - EntitlementService.getActiveModuleKeys)
 *    - radi i za legacy firme (CompanyModule sistem) i za firme sa
 *    Subscription-om (Package/Addon sistem).
 * 3) Prazna checklist modula po paketu (kad `packageModules` tabela u bazi
 *    nije popunjena ni za jedan paket) sada ima honest poruku umesto tihe
 *    prazne liste - ovo JE DATA/ADMIN konfiguracija, ne bag stranice.
 * 4) Aktivan dodatni modul se prikazuje PLAVOM (brand) bojom umesto zelene -
 *    "aktivan" treba vizuelno da bude ista boja kao trenutni paket/brand,
 *    ne generička "success" zelena.
 * 5) Tab "Finora ERP"/"Finora API" - promenjen na underline stil (donja
 *    plava linija ispod aktivnog taba), bliže mockup-u; tabovi su sada IZNAD
 *    dvokolonskog grid-a (ne u levoj koloni), tako da "Vaš izbor" panel
 *    počinje na ISTOJ visini kao kartice paketa.
 *
 * NAMERNA UPROŠĆENJA (obeleženo i korisniku, čeka "spajanje sa admin delom"):
 * a) (rešeno 23.09.2026) "Finora ERP" / "Finora API" tabovi sada filtriraju
 *    stvarne pakete po Package.family (podešava se u Admin -> Paketi) -
 *    tab više NE prikazuje statični "uskoro dostupno" stub.
 * b) Paketi u bazi nemaju polje za kratak opis/"tagline" ispod imena.
 * c) Pošto ne postoji JEDAN "checkout" API poziv koji kombinuje promenu
 *    paketa + više addon-a u JEDNU ponudu, "Sačuvaj promene" redom kreira
 *    po JEDNU ponudu za paket (ako je promenjen) i po JEDNU za svaki novi
 *    modul. Ako nastane VIŠE OD JEDNE ponude, korisnik NIJE automatski
 *    odveden na platnu stranicu (ne može na dve odjednom) - umesto toga,
 *    ispod panela se prikazuje lista linkova ka svakoj kreiranoj ponudi.
 *    Ako je kreirana samo JEDNA ponuda, korisnik se odmah vodi na nju (isto
 *    ponašanje kao pre).
 * d) Aktivan dodatni modul se i dalje NE može isključiti sa ovog ekrana
 *    (nema postojećeg API poziva za to) - prekidač je zaključan kad je
 *    modul već aktivan.
 */

const PACKAGE_VISUALS: Array<{
  match: (key: string) => boolean;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
}> = [
  { match: (k) => k.includes("start"), icon: Sprout, iconBg: "bg-emerald-50", iconColor: "text-emerald-600" },
  { match: (k) => k.includes("basic"), icon: Crown, iconBg: "bg-amber-50", iconColor: "text-amber-600" },
  { match: (k) => k.includes("enterprise"), icon: Building2, iconBg: "bg-slate-100", iconColor: "text-slate-600" },
  { match: (k) => k.includes("business"), icon: Rocket, iconBg: "bg-violet-50", iconColor: "text-violet-600" },
  { match: (k) => k.includes("pro"), icon: TrendingUp, iconBg: "bg-sky-50", iconColor: "text-sky-600" },
];
const DEFAULT_PACKAGE_VISUAL = { icon: PackageIcon, iconBg: "bg-slate-100", iconColor: "text-slate-600" };
function getPackageVisual(key: string) {
  const k = key.toLowerCase();
  return PACKAGE_VISUALS.find((v) => v.match(k)) ?? DEFAULT_PACKAGE_VISUAL;
}

const MODULE_ICONS: Array<{ match: (key: string) => boolean; icon: LucideIcon }> = [
  { match: (k) => k.startsWith("kasa"), icon: ShoppingCart },
  { match: (k) => k.startsWith("magacin"), icon: Warehouse },
  { match: (k) => k.startsWith("troskovi"), icon: Wallet },
  { match: (k) => k.includes("bank"), icon: Landmark },
  { match: (k) => k.startsWith("efaktur"), icon: FileText },
  { match: (k) => k.includes("minimax"), icon: Link2 },
  { match: (k) => k.startsWith("artikli"), icon: Tag },
  { match: (k) => k.includes("mobile"), icon: Smartphone },
  { match: (k) => k.startsWith("api"), icon: Code2 },
];
function getModuleIcon(key: string): LucideIcon {
  const k = key.toLowerCase();
  return (MODULE_ICONS.find((m) => m.match(k)) ?? { icon: Puzzle }).icon;
}

// Granica prorate - MORA ostati usklađena sa backend-om
// (SalesDocumentsService.getProrationWindow) - razgovor 23.09.2026: "pro
// rata ide do 25 u meseci od 26 se uzima taj mesec plus sledeci". Ovo je
// SAMO informativni prikaz na "Moj paket" (okvirna procena na osnovu
// današnjeg datuma) - tačan iznos uvek definiše stvarna ponuda koju kreira
// backend kad korisnik klikne "Sačuvaj promene".
const PRORATION_BUNDLE_FROM_DAY = 26;

interface ProrationPreview {
  todayAmount: number;
  nextMonthAmount: number;
  explanation: string;
}

/**
 * Preview "zaduženje danas" / "zaduženje za sledeći mesec" za paket koji je
 * korisnik stavio u izbor (stagedPackage) I za svaki NOVO izabrani addon
 * modul sa mesečnom pretplatom (stagedAddons, billingType MONTHLY) - vidi
 * razgovor 23.09.2026, "mora da stoji kada se udje u moj paket zaduzenje
 * danas obracunato na osnovu prorata, i zaduzenje za sledeci mesec uz
 * objasnjenje", i naknadna dopuna istog dana: "nisi promenio da addon
 * kupovina prati istu prorata logiku" - preview je u prvoj verziji računao
 * SAMO stagedPackage, addon modul kupljen samostalno (bez promene paketa)
 * nije uopšte prikazivao ovaj panel niti prorata-ovao cenu.
 *
 * Logika prati calculateMonthlyProration/getProrationWindow na backend-u
 * (SalesDocumentsService) - prorata se računa na NETO ceni pa se PDV
 * dodaje na vrh (grossFromNet), a od 26. u mesecu se ostatak tekućeg
 * meseca i ceo naredni mesec naplaćuju ODJEDNOM - pa je tada "zaduženje za
 * sledeći mesec" 0 (već je plaćeno danas), a sledeća redovna naplata sledi
 * tek mesec posle narednog. Usage-based addon moduli (billingType različit
 * od MONTHLY, npr. PER_TRANSACTION/PER_API_CALL) se ne prorata-uju i ne
 * ulaze u ovaj preview - nemaju "mesečni period".
 */
function getProrationPreview(
  pkg: { price: string; priceGross: string; billingPeriod: string } | null | undefined,
  monthlyAddons: Array<{ price: string; priceGross: string; billingType: string }>,
): ProrationPreview | null {
  const items: Array<{ net: number; gross: number }> = [];
  if (pkg && pkg.billingPeriod === "MONTHLY") {
    items.push({ net: Number(pkg.price), gross: Number(pkg.priceGross) });
  }
  for (const m of monthlyAddons) {
    if (m.billingType === "MONTHLY") {
      items.push({ net: Number(m.price), gross: Number(m.priceGross) });
    }
  }
  if (items.length === 0) return null;

  const fullNet = items.reduce((sum, it) => sum + it.net, 0);
  const fullGross = items.reduce((sum, it) => sum + it.gross, 0);

  const now = new Date();
  const totalDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const day = now.getDate();
  const remainingDays = totalDays - day + 1;
  const includesNextMonth = day >= PRORATION_BUNDLE_FROM_DAY;

  if (remainingDays >= totalDays && !includesNextMonth) {
    // 1. u mesecu - ceo mesec je "preostao", nema prorate.
    return {
      todayAmount: fullGross,
      nextMonthAmount: fullGross,
      explanation: "Danas je 1. u mesecu, pa se odmah naplaćuje puna mesečna cena - isto i sledećeg meseca.",
    };
  }

  // Gross se prorata-uje proporcionalno danima - matematički identično
  // prorata-ovanju neto cene pa dodavanju PDV-a na vrh (grossFromNet), jer
  // je gross = net * (1 + stopa), pa razmera ostaje ista.
  const proratedGross = (fullGross * remainingDays) / totalDays;

  if (!includesNextMonth) {
    return {
      todayAmount: proratedGross,
      nextMonthAmount: fullGross,
      explanation: `Naplaćuje se samo preostalih ${remainingDays} od ${totalDays} dana u ovom mesecu (proporcionalno); od sledećeg meseca naplaćuje se puna mesečna cena.`,
    };
  }

  return {
    todayAmount: proratedGross + fullGross,
    nextMonthAmount: 0,
    explanation: `Posle 25. u mesecu prorata odmah uključuje i naredni mesec: danas se naplaćuje ostatak ovog meseca (${remainingDays} od ${totalDays} dana) plus ceo naredni mesec unapred, pa sledeća redovna naplata sledi tek mesec posle narednog.`,
  };
}

export function MyPackagePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [family, setFamily] = useState<"erp" | "api">("erp");

  // Staged izbor - NIŠTA od ovoga ne zove API dok korisnik ne klikne
  // "Sačuvaj promene" (vidi napomena #1 na vrhu fajla).
  const [stagedPackageId, setStagedPackageId] = useState<string | null>(null);
  const [stagedAddonKeys, setStagedAddonKeys] = useState<string[]>([]);
  // Moduli koje je korisnik sam uključio (ostali u listi su automatski dodati kao zavisnost).
  const manualAddonKeys = useRef<Set<string>>(new Set());
  const [createdQuotes, setCreatedQuotes] = useState<Array<{ label: string; token: string }>>([]);

  const { data, isLoading } = useQuery({
    queryKey: ["my-package"],
    queryFn: () => api.getMyPackage(),
  });
  const { data: entitlements } = useQuery({
    queryKey: ["my-entitlements"],
    queryFn: () => api.getEntitlements(),
  });

  // Ponuda za promenu paketa/kupovinu addon-a se izdaje kod MATIČNE Finora
  // firme, ne kod ove firme (klijenta) - klijent nema pristup /prodaja/:id
  // tuđeg tenant-a, pa ga vodimo na javnu stranicu ponude (isti link koji bi
  // dobio i svaki drugi kupac kome se pošalje ponuda).
  const saveChanges = useMutation({
    mutationFn: async () => {
      const quotes: Array<{ label: string; doc: SalesDocumentDto }> = [];
      if (stagedPackageId) {
        const doc = await api.changePackage(stagedPackageId);
        quotes.push({ label: "Promena paketa", doc });
      }
      // Prvo moduli koje drugi zahtevaju (zavisnosti), ostali posle njih.
      const catalog = data?.addonModules ?? [];
      const ordered = [...stagedAddonKeys].sort((a, b) => {
        const aNeedsB = catalog.find((m) => m.key === a)?.requiredModules.some((r) => r.key === b);
        const bNeedsA = catalog.find((m) => m.key === b)?.requiredModules.some((r) => r.key === a);
        return aNeedsB ? 1 : bNeedsA ? -1 : 0;
      });
      for (const moduleKey of ordered) {
        const doc = await api.purchaseAddon(moduleKey, stagedAddonKeys.filter((k) => k !== moduleKey));
        quotes.push({ label: `Modul: ${moduleKey}`, doc });
      }
      return quotes;
    },
    onSuccess: (quotes) => {
      queryClient.invalidateQueries({ queryKey: ["my-package"] });
      queryClient.invalidateQueries({ queryKey: ["my-entitlements"] });
      setStagedPackageId(null);
      setStagedAddonKeys([]);
      manualAddonKeys.current = new Set();
      const withToken = quotes.filter((q) => q.doc.publicLinkToken);
      const [firstQuote] = withToken;
      if (withToken.length === 1 && firstQuote) {
        navigate(`/pregled/ponuda/${firstQuote.doc.publicLinkToken}`);
      } else if (withToken.length > 1) {
        // Nema jednog "checkout" poziva koji kombinuje sve u JEDNU ponudu
        // (vidi napomena c) - kreirano je više ponuda, ne možemo korisnika
        // odvesti na dve stranice odjednom, pa mu dajemo listu linkova.
        setCreatedQuotes(withToken.map((q) => ({ label: q.label, token: q.doc.publicLinkToken as string })));
      }
    },
  });

  const toggleStagedPackage = (packageId: string) => {
    setStagedPackageId((prev) => (prev === packageId ? null : packageId));
  };
  // Zavisni moduli: uključivanje addon-a automatski dodaje u izbor i module koje on
  // zahteva (ako nisu već aktivni), a uklanjanje zahtevanog modula uklanja i one koji ga traže.
  const toggleStagedAddon = (moduleKey: string) => {
    const catalog = data?.addonModules ?? [];
    const activeKeys = new Set((data?.activeModules ?? []).map((m) => m.key));
    setStagedAddonKeys((prev) => {
      if (prev.includes(moduleKey)) {
        const removed = new Set([moduleKey]);
        let changed = true;
        while (changed) {
          changed = false;
          for (const m of catalog) {
            if (!removed.has(m.key) && prev.includes(m.key) && m.requiredModules.some((r) => removed.has(r.key))) {
              removed.add(m.key);
              changed = true;
            }
          }
        }
        const kept = prev.filter((k) => !removed.has(k));
        for (const k of removed) manualAddonKeys.current.delete(k);
        const result = new Set<string>();
        const keep = (key: string) => {
          if (result.has(key) || activeKeys.has(key)) return;
          result.add(key);
          for (const r of catalog.find((m) => m.key === key)?.requiredModules ?? []) {
            if (catalog.some((m) => m.key === r.key)) keep(r.key);
          }
        };
        for (const k of kept) if (manualAddonKeys.current.has(k)) keep(k);
        return [...result];
      }
      manualAddonKeys.current.add(moduleKey);
      const next = new Set(prev);
      const add = (key: string) => {
        if (next.has(key) || activeKeys.has(key)) return;
        next.add(key);
        for (const r of catalog.find((m) => m.key === key)?.requiredModules ?? []) {
          if (catalog.some((m) => m.key === r.key)) add(r.key);
        }
      };
      add(moduleKey);
      return [...next];
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const { current, packages, addonModules, activeModules, taxRatePercent } = data ?? {
    current: null,
    packages: [],
    addonModules: [],
    activeModules: [],
    taxRatePercent: "0",
  };

  // "HAfiscal ERP" vs "HAfiscal API" tab - filtrirano po Package.family (vidi
  // razgovor 23.09.2026, "checkbox da li je ERP ili je finora api"). Ranije
  // ovo polje nije postojalo u bazi, pa je "HAfiscal API" tab bio trajni
  // "uskoro dostupno" stub.
  const visiblePackages = packages.filter((p) => p.family === (family === "erp" ? "ERP" : "API"));

  // Unija svih modula preko paketa TRENUTNO PRIKAZANE porodice - redovi
  // feature liste na karticama.
  const allModules = new Map<string, { key: string; name: string }>();
  for (const pkg of visiblePackages) {
    for (const m of pkg.includedModules) {
      allModules.set(m.id, { key: m.key, name: m.name });
    }
  }
  const moduleRows = Array.from(allModules.entries());

  const activeAddons = addonModules.filter((m) => m.isActive);
  const stagedAddons = addonModules.filter((m) => stagedAddonKeys.includes(m.key));
  // Sabiraju se GROSS ("sa PDV-om") vrednosti koje je backend već izračunao
  // po stavci (grossFromNet) - isto kao što će stvarna ponuda biti obračunata
  // (svaka pozicija zasebno, ne PDV na zbir neto cena) - vidi razgovor
  // 23.09.2026 "prikaz pdva kada se poručuju paketi".
  const stagedAddonsTotal = stagedAddons.reduce((sum, m) => sum + Number(m.priceGross || "0"), 0);
  const stagedPackage = stagedPackageId ? packages.find((p) => p.id === stagedPackageId) : null;
  const extraUsers = entitlements?.users ? Math.max(0, entitlements.users.used - entitlements.users.included) : 0;
  const extraUsersPriceGross = entitlements?.users ? Number(entitlements.users.additionalPriceGross || "0") : 0;
  const extraUsersTotal = extraUsers * extraUsersPriceGross;
  const basePackagePrice = stagedPackage
    ? Number(stagedPackage.priceGross)
    : current
      ? Number(current.priceGross)
      : 0;
  const totalMonthly = basePackagePrice + stagedAddonsTotal + extraUsersTotal;
  const hasStagedChanges = Boolean(stagedPackageId) || stagedAddonKeys.length > 0;

  // Raščlanjivanje "Vaš izbor" panela na osnovicu/PDV/ukupno (razgovor
  // 23.09.2026, "U obračunu desno cene bez pdv, onda pdv 20% i iznos pdv-a i
  // na kraju total") - iste NETO ("bez PDV-a") vrednosti koje su već
  // korišćene za sekundarnu liniju cene na karticama paketa/modula
  // (pkg.price, mod.price, additionalPrice), umesto ponovnog izračunavanja
  // PDV-a na frontend-u. `taxAmount` se namerno računa kao totalMonthly minus
  // osnovica (ne osnovica * stopa) da bi zbir stavki u panelu uvek TAČNO
  // odgovarao već prikazanom "Ukupno mesečno" iznosu.
  const basePackagePriceNet = stagedPackage
    ? Number(stagedPackage.price)
    : current
      ? Number(current.price)
      : 0;
  const stagedAddonsTotalNet = stagedAddons.reduce((sum, m) => sum + Number(m.price || "0"), 0);
  const extraUsersPriceNet = entitlements?.users ? Number(entitlements.users.additionalPrice || "0") : 0;
  const extraUsersTotalNet = extraUsers * extraUsersPriceNet;
  const netTotal = basePackagePriceNet + stagedAddonsTotalNet + extraUsersTotalNet;
  const taxAmount = Math.max(totalMonthly - netTotal, 0);

  // "Zaduženje danas" / "zaduženje za sledeći mesec" - samo dok je paket u
  // izboru (stagedPackage) i/ili novih mesečnih addon modula (stagedAddons),
  // vidi getProrationPreview.
  const prorationPreview = getProrationPreview(stagedPackage, stagedAddons);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-light text-brand">
            <PackageIcon className="h-6 w-6" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Moj paket</h1>
            {current ? (
              <p className="text-sm text-slate-600">
                Vaš trenutni paket je <span className="font-semibold text-slate-900">{current.packageName}</span>.
              </p>
            ) : (
              <p className="text-sm text-slate-500">Izaberite paket i module koji odgovaraju vašem poslovanju.</p>
            )}
            {current && (
              <p className="mt-1 text-xs text-slate-400">
                Status: {STATUS_LABELS[current.status] ?? current.status}
                {current.trialEndsAt && current.status === "TRIAL"
                  ? ` do ${new Date(current.trialEndsAt).toLocaleDateString("sr-RS")}`
                  : ""}
                {" · "}Tekući period: {new Date(current.currentPeriodStart).toLocaleDateString("sr-RS")} –{" "}
                {new Date(current.currentPeriodEnd).toLocaleDateString("sr-RS")}
              </p>
            )}
            {!current && activeModules.length > 0 && (
              <p className="mt-1 text-xs text-slate-400">
                Firma još nije prebačena na novi sistem paketa - modul(i) ispod su aktivni po dogovoru sa podrškom.
              </p>
            )}
            {activeModules.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {activeModules.map((m) => (
                  <span
                    key={m.key}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-light px-2 py-0.5 text-xs font-medium text-brand"
                  >
                    <Check className="h-3 w-3" aria-hidden /> {m.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={() => (window.location.href = SUPPORT_MAILTO)}>
          <HelpCircle className="h-4 w-4" aria-hidden /> Pomoć oko paketa
        </Button>
      </div>

      <KasaLpfrCard />

      <div className="flex gap-6 border-b border-border">
        <button
          type="button"
          onClick={() => setFamily("erp")}
          className={`flex items-center gap-2 border-b-2 pb-3 text-sm font-semibold transition-colors ${
            family === "erp" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Building2 className="h-4 w-4" aria-hidden /> HAFiskal ERP
        </button>
        <button
          type="button"
          onClick={() => setFamily("api")}
          className={`flex items-center gap-2 border-b-2 pb-3 text-sm font-semibold transition-colors ${
            family === "api" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Code2 className="h-4 w-4" aria-hidden /> HAFiskal API
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
        <div className="space-y-6 lg:col-span-2">
          {saveChanges.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {saveChanges.error instanceof ApiError
                ? saveChanges.error.response.message
                : "Čuvanje promena nije uspelo."}
            </p>
          )}

          {createdQuotes.length > 0 && (
            <div className="rounded-lg bg-brand-light px-4 py-3 text-sm text-slate-700">
              <p className="font-medium text-slate-900">Kreirane su {createdQuotes.length} ponude za plaćanje:</p>
              <ul className="mt-2 space-y-1">
                {createdQuotes.map((q) => (
                  <li key={q.token}>
                    <a
                      href={`/pregled/ponuda/${q.token}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                    >
                      {q.label} <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {visiblePackages.length === 0 ? (
            <p className="rounded-xl border border-border bg-surface p-5 text-sm text-slate-400 shadow-subtle">
              {family === "erp"
                ? "Trenutno nema aktivnih paketa za prikaz."
                : "Trenutno nema aktivnih HAFiskal API paketa - javite se podršci za pristup partnerskom API-ju."}
            </p>
          ) : (
            <>
              {moduleRows.length === 0 && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Poređenje modula po paketu još nije podešeno (moduli nisu povezani sa paketima u admin panelu) -
                  kartice ispod prikazuju samo cenu i broj uključenih korisnika.
                </p>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {visiblePackages.map((pkg) => {
                    const visual = getPackageVisual(pkg.key);
                    const Icon = visual.icon;
                    // Nema posebnog polja u bazi za "kontaktirajte nas" pakete
                    // (npr. Enterprise) - najbolja dostupna pretpostavka: cena
                    // 0 i BEZ probnog perioda znači "po dogovoru" (za razliku
                    // od besplatnog trial paketa, koji ima trialEnabled=true).
                    const isContactOnly = Number(pkg.price) === 0 && !pkg.trialEnabled;
                    const isStaged = stagedPackageId === pkg.id;
                    return (
                      <div
                        key={pkg.id}
                        className={`flex flex-col rounded-xl border p-5 shadow-subtle ${
                          pkg.isCurrent
                            ? "border-brand bg-white ring-1 ring-brand"
                            : isStaged
                              ? "border-brand bg-brand-light/40 ring-1 ring-brand"
                              : "border-border bg-surface"
                        }`}
                      >
                        {/* Značka (TRENUTNI PAKET / U VAŠEM IZBORU) je NAMERNO u
                            ISTOM redu kao ikonica (ne iznad nje) - vidi razgovor
                            "kada se klikne na to ne pomera text na dole". Visina
                            reda je fiksirana ikonicom (h-11 = 44px), pa
                            pojavljivanje/nestajanje značke (koja je niža od 44px)
                            ne menja visinu reda i ne pomera ništa ispod. */}
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${visual.iconBg} ${visual.iconColor}`}
                          >
                            <Icon className="h-5 w-5" aria-hidden />
                          </div>
                          {pkg.isCurrent && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-xs font-semibold text-white">
                              <Check className="h-3 w-3" aria-hidden /> TRENUTNI PAKET
                            </span>
                          )}
                          {!pkg.isCurrent && isStaged && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">
                              U VAŠEM IZBORU
                            </span>
                          )}
                        </div>
                        <p className="text-base font-bold text-slate-900">{pkg.name}</p>

                        <p className="mb-1 mt-2 text-2xl font-extrabold text-slate-900">
                          {isContactOnly ? "Po dogovoru" : formatMoney(pkg.price)}
                        </p>
                        {!isContactOnly && (
                          <p className="mb-3 text-xs text-slate-400">
                            /{pkg.billingPeriod === "YEARLY" ? "godišnje" : "mesečno"} · bez PDV-a (
                            {formatMoney(pkg.priceGross)} sa PDV-a)
                          </p>
                        )}

                        {moduleRows.length > 0 && (
                          <ul className="mb-4 flex-1 space-y-1.5 text-sm">
                            {moduleRows.map(([moduleId, mod]) => {
                              const included = pkg.includedModules.some((m) => m.id === moduleId);
                              return (
                                <li key={moduleId} className="flex items-center gap-2">
                                  {included ? (
                                    <Check className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden />
                                  ) : (
                                    <X className="h-3.5 w-3.5 shrink-0 text-slate-300" aria-hidden />
                                  )}
                                  <span className={included ? "text-slate-700" : "text-slate-400"}>{mod.name}</span>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        <p className="mb-3 text-xs text-slate-400">
                          {pkg.includedUsers} {pkg.includedUsers === 1 ? "korisnik" : "korisnika"} uključeno
                        </p>

                        {pkg.isCurrent ? (
                          <Button disabled variant="secondary" className="w-full justify-center">
                            <Check className="h-4 w-4" aria-hidden /> Trenutni paket
                          </Button>
                        ) : isContactOnly ? (
                          <Button
                            variant="secondary"
                            className="w-full justify-center"
                            onClick={() => (window.location.href = SUPPORT_MAILTO)}
                          >
                            Kontaktirajte nas
                          </Button>
                        ) : (
                          <Button
                            variant={isStaged ? "secondary" : "primary"}
                            className="w-full justify-center"
                            onClick={() => toggleStagedPackage(pkg.id)}
                          >
                            {isStaged ? (
                              <>
                                <Check className="h-4 w-4" aria-hidden /> Dodato u izbor
                              </>
                            ) : current ? (
                              "Promeni paket"
                            ) : (
                              "Izaberi paket"
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              {/* Jedna jasno vidljiva napomena ISPOD svih kartica (ne sitan
                  tekst po kartici - vidi razgovor 23.09.2026) - važi za sve
                  pakete odjednom, pa je dovoljno reći to jednom. */}
              <p className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
                Prikazane cene su BEZ PDV-a; cena sa uračunatim PDV-om (stopa {taxRatePercent}%) - iznos koji ćete
                platiti - prikazana je ispod nje radi poređenja.
              </p>
            </>
          )}

          <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
                <Puzzle className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Dodatni moduli</p>
                <p className="text-xs text-slate-500">
                  Proširite mogućnosti sistema dodavanjem dodatnih modula. Cena modula se dodaje na cenu izabranog
                  paketa.
                </p>
              </div>
            </div>

            {addonModules.length === 0 ? (
              <p className="mt-4 text-sm text-slate-400">Trenutno nema modula dostupnih za samostalnu kupovinu.</p>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {addonModules.map((mod) => {
                  const Icon = getModuleIcon(mod.key);
                  const isStaged = stagedAddonKeys.includes(mod.key);
                  // Aktivan (plavo, kao brand) ILI staged za kupovinu (takođe
                  // plavo, ali prekidač nije "zaključan" kao kod aktivnog).
                  const isOn = mod.isActive || isStaged;
                  // Zahtevani moduli koji nisu aktivni, niti se mogu dokupiti ovde (treba jači paket).
                  const activeKeySet = new Set(activeModules.map((a) => a.key));
                  const missingRequired = mod.requiredModules.filter((r) => !activeKeySet.has(r.key));
                  const blocked = missingRequired.some((r) => !addonModules.some((a) => a.key === r.key));
                  return (
                    <div
                      key={mod.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border bg-app-bg px-3 py-2.5"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 shadow-subtle">
                          <Icon className="h-4 w-4" aria-hidden />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{mod.name}</p>
                          {mod.requiredModules.length > 0 && !mod.isActive && (
                            <p className="text-xs text-amber-600">
                              Zahteva: {mod.requiredModules.map((r) => r.name).join(", ")}
                              {blocked ? " (nije u vašem paketu)" : missingRequired.length > 0 ? " (dodaje se automatski)" : ""}
                            </p>
                          )}
                          {mod.isActive ? (
                            <p className="text-xs text-slate-400">Aktivan</p>
                          ) : (
                            <p className="text-xs text-slate-400">
                              {formatMoney(mod.price)} mesečno
                              <br />
                              (bez PDV-a, {formatMoney(mod.priceGross)} sa PDV-a)
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isOn}
                        disabled={mod.isActive || (blocked && !isStaged)}
                        title={
                          mod.isActive
                            ? "Modul je već aktivan (isključivanje ovde još nije podržano - javite se podršci)"
                            : isStaged
                              ? "Dodato u 'Vaš izbor' - ukloni klikom, ili sačuvaj promene za kreiranje ponude"
                              : "Dodaj u 'Vaš izbor'"
                        }
                        onClick={() => toggleStagedAddon(mod.key)}
                        className={`relative h-6 w-11 shrink-0 overflow-hidden rounded-full transition-colors disabled:cursor-default ${
                          isOn ? "bg-brand" : "cursor-pointer bg-slate-200 hover:bg-slate-300"
                        }`}
                      >
                        {/* Pozicija knopke se namerno računa preko inline
                            `style.left` (ne Tailwind translate-x-* klase) -
                            vidi razgovor "slajder je do kraja i siv je... kada
                            ga kliknem izadje iz okvira". Ovako je garantovano
                            NEZAVISNO od bilo kakvog build/purge ponašanja:
                            isključeno (isOn=false) -> knopka skroz LEVO, sivo;
                            uključeno (isOn=true) -> knopka skroz DESNO, plavo. */}
                        <span
                          className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-150"
                          style={{ left: isOn ? "22px" : "2px" }}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-surface p-5 shadow-subtle sm:grid-cols-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                <Sliders className="h-4 w-4" aria-hidden />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800">Fleksibilno</p>
                <p className="text-xs text-slate-400">Dodajte ili uklonite module kada vam zatreba.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Eye className="h-4 w-4" aria-hidden />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800">Transparentne cene</p>
                <p className="text-xs text-slate-400">Jasan pregled svih troškova.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-50 text-violet-600">
                <Headset className="h-4 w-4" aria-hidden />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800">Podrška</p>
                <p className="text-xs text-slate-400">Tu smo da vam pomognemo oko izbora.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-4">
          <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
            <div className="mb-3 flex items-center gap-2">
              <ShoppingBag className="h-4 w-4 text-slate-400" aria-hidden />
              <p className="font-semibold text-slate-900">Vaš izbor</p>
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-500">Paket</dt>
                <dd className="text-right font-medium text-slate-800">
                  {stagedPackage ? (
                    <>
                      {stagedPackage.name}{" "}
                      <span className="text-xs font-normal text-brand">(novo)</span>
                      <br />
                      <span className="text-xs font-normal text-slate-400">
                        {formatMoney(stagedPackage.priceGross)} sa PDV-om
                      </span>
                    </>
                  ) : current ? (
                    <>
                      {current.packageName}
                      <br />
                      <span className="text-xs font-normal text-slate-400">
                        {formatMoney(current.priceGross)} sa PDV-om
                      </span>
                    </>
                  ) : (
                    "Nije izabran"
                  )}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-2">
                <dt className="text-slate-500">Dodatni moduli</dt>
                <dd className="text-right font-medium text-slate-800">
                  {activeAddons.length === 0 && stagedAddons.length === 0 ? (
                    "Nema"
                  ) : (
                    <div className="space-y-1">
                      {activeAddons.map((m) => (
                        <div key={m.id} className="flex items-center justify-between gap-3">
                          <span className="text-slate-700">{m.name}</span>
                          <span className="text-xs font-normal text-slate-400">Aktivan</span>
                        </div>
                      ))}
                      {stagedAddons.map((m) => (
                        <div key={m.id} className="flex items-center justify-between gap-3">
                          <span className="text-brand">{m.name}</span>
                          <span className="text-xs font-normal text-brand">{formatMoney(m.price)} bez PDV-a</span>
                        </div>
                      ))}
                    </div>
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-500">Dodatni korisnici</dt>
                <dd className="text-right font-medium text-slate-800">
                  {extraUsers === 0 ? "0" : `${extraUsers} · ${formatMoney(String(extraUsersTotal))} sa PDV-om`}
                </dd>
              </div>
            </dl>

            {prorationPreview && (
              <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2.5 text-amber-900">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Proporcionalna naplata</p>
                <dl className="mt-1.5 space-y-1 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <dt>Zaduženje danas</dt>
                    <dd className="font-semibold">{formatMoney(String(prorationPreview.todayAmount))}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt>Zaduženje za sledeći mesec</dt>
                    <dd className="font-semibold">{formatMoney(String(prorationPreview.nextMonthAmount))}</dd>
                  </div>
                </dl>
                <p className="mt-1.5 text-xs leading-snug text-amber-800">{prorationPreview.explanation}</p>
              </div>
            )}

            <div className="my-3 border-t border-border" />
            <dl className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-500">Osnovica (bez PDV-a)</dt>
                <dd className="font-medium text-slate-700">{formatMoney(String(netTotal))}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-500">PDV ({taxRatePercent}%)</dt>
                <dd className="font-medium text-slate-700">{formatMoney(String(taxAmount))}</dd>
              </div>
            </dl>
            <div className="my-3 border-t border-border" />
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">Ukupno mesečno (sa PDV-om)</p>
              <p className="text-lg font-bold text-slate-900">{formatMoney(String(totalMonthly))}</p>
            </div>

            <Button
              className="mt-4 w-full justify-center"
              disabled={!hasStagedChanges || saveChanges.isPending}
              onClick={() => saveChanges.mutate()}
            >
              <Check className="h-4 w-4" aria-hidden />
              {saveChanges.isPending ? "Kreiranje ponude..." : "Sačuvaj promene"}
            </Button>

            <div className="mt-4 flex gap-2.5 rounded-lg bg-sky-50 px-3 py-3 text-sky-800">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" aria-hidden />
              <div>
                <p className="text-sm font-semibold text-sky-900">Promena paketa i modula</p>
                <p className="mt-1 text-[13px] leading-snug text-sky-700">
                  {hasStagedChanges
                    ? "Klik na \"Sačuvaj promene\" kreira ponudu za plaćanje - paket ili modul se aktivira čim se ta ponuda u potpunosti naplati."
                    : "Izaberite paket ili dodatni modul da ga dodate u izbor, pa kliknite \"Sačuvaj promene\" kad budete spremni."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
